import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { normalizeToUtc } from '@asp/domain';
import type { CalendarEntry, ProjectListRow } from '../api/client.js';
import { cancelSchedule, fetchCalendar, fetchProjects, scheduleVariant } from '../api/client.js';
import type { DayCell } from '../calendar/grid.js';
import { monthCells, rangeFor, shiftAnchor, weekCells } from '../calendar/grid.js';
import type { DragState } from '../calendar/drag.js';
import { onPointerDown, onPointerMove, onPointerUp } from '../calendar/drag.js';
import { RescheduleConfirm } from '../components/RescheduleConfirm.js';
import { Stamp } from '../components/Stamp.js';
import { StateChip } from '../components/StateChip.js';
import { useDictionary, useLocale } from '../i18n/context.js';
import type { Dictionary, ScheduleStateKey } from '../i18n/types.js';

// Fix round 1: the range heading and the list view's day headers used to
// call `Intl.DateTimeFormat(undefined, ...)`, which formats using the
// *browser's* locale, not the app's own UI language -- under a Japanese UI
// with an English-locale browser the heading kept reading "September 2026"
// regardless of which locale toggle (Shell.tsx's own `locale-toggle`) the
// operator had selected. `Stamp.tsx` sidesteps this by pinning 'en-US'
// outright (it always shows the dual UTC/local clock in one fixed format);
// this screen's headings are prose next to a `useDictionary()`-driven UI,
// so they follow the same locale instead of a third, independent choice.
const INTL_LOCALE: Record<'en' | 'ja', string> = { en: 'en-US', ja: 'ja-JP' };

// ---------------------------------------------------------------------------
// Calendar (UI structure spec 9): the last primary-nav screen. Structure,
// in order (9.2): scope/date-range heading; week/month/list switch;
// previous/current/next navigation; project/platform/state filters;
// browser-time indicator; the content (a grid or a date-grouped list); the
// selected-entry detail. Bulk controls (9.2's own parenthetical) are out of
// scope -- no disabled shell is rendered for them.
//
// Two decisions this task settles, both explained in full where they act:
//
// 1. The 'list' view's date span (Task 3's `rangeFor` already guessed
//    month, in a comment on that function). Kept here: paging list by
//    month, grouped by date within the page, matches "date-grouped list...
//    supports date navigation" (9.5) without inventing a second navigation
//    granularity the spec never names. A rolling window from today was the
//    other real option; month was chosen because it reuses the exact
//    previous/current/next semantics the week and month views already
//    have, rather than adding a third kind of "next" specific to list.
//
// 2. Client-side filtering (see `filterEntries` below and the header
//    comment on `fetchCalendar` in api/client.ts): this screen always
//    fetches the *unfiltered* range and applies project/platform/state
//    filters in the browser, because 9.6 requires distinguishing "nothing
//    in range" from "something is here, but a filter is hiding it," which
//    is only knowable if the unfiltered set is seen at all.
// ---------------------------------------------------------------------------

export type CalendarView = 'week' | 'month' | 'list';
const VIEWS: readonly CalendarView[] = ['week', 'month', 'list'];

/** Defaults to 'week' (spec 9.2's stated default) for anything absent or unrecognized. */
export const parseView = (raw: string | null): CalendarView =>
  raw !== null && (VIEWS as readonly string[]).includes(raw) ? (raw as CalendarView) : 'week';

/** A browser-local calendar-day key, stable for grouping/matching entries to grid cells without ever going through a display string. */
export const localDayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export type CalendarFilters = { projectId: string; platform: string; state: string };

export const filterEntries = (entries: CalendarEntry[], filters: CalendarFilters): CalendarEntry[] =>
  entries.filter(
    (e) =>
      (filters.projectId === '' || e.projectId === filters.projectId) &&
      (filters.platform === '' || e.platform === filters.platform) &&
      (filters.state === '' || e.scheduleState === filters.state),
  );

export type RangeStatus = 'error' | 'empty' | 'filtered_out' | 'has_entries';

/**
 * Spec 9.6's three states, plus the ordinary "there is content" case.
 * `loadError` wins over everything -- a failed range shouldn't also claim
 * "nothing is scheduled." Otherwise: `rawCount` (the unfiltered fetch)
 * decides "empty" vs "something exists"; `filteredCount` then decides
 * whether the active filters are hiding what exists. This is the pure
 * decision behind the two visually distinct empty states 9.6 requires.
 */
export const rangeStatus = (rawCount: number, filteredCount: number, loadError: boolean): RangeStatus => {
  if (loadError) return 'error';
  if (rawCount === 0) return 'empty';
  if (filteredCount === 0) return 'filtered_out';
  return 'has_entries';
};

/**
 * Where a dragged entry lands: `targetDay`'s calendar date, `original`'s
 * local time-of-day (including milliseconds) -- built entirely from Date
 * field getters/the local constructor, never from a rendered string, so a
 * drag that doesn't change the time-of-day can't lose precision.
 */
export const computeDroppedInstant = (original: Date, targetDay: Date): Date =>
  new Date(
    targetDay.getFullYear(),
    targetDay.getMonth(),
    targetDay.getDate(),
    original.getHours(),
    original.getMinutes(),
    original.getSeconds(),
    original.getMilliseconds(),
  );

// Builds the RFC 3339 string actually sent to the server, fresh from the
// Date object the confirmation just handed back -- never from anything
// this screen renders. `Date#toISOString()` already carries full
// millisecond precision and a trailing 'Z'; there is no local offset to
// encode here the way SchedulePanel's own `toRfc3339` has to (that one
// starts from a zoneless `datetime-local` string), because every `to`
// this screen produces -- from a drag or from the keyboard path below --
// is already a complete Date instance.
const rfc3339FromInstant = (date: Date): string => date.toISOString();

// The keyboard-accessible reschedule path's own input is a zoneless
// `datetime-local` value, exactly like SchedulePanel's -- so it needs the
// same local-offset encoding SchedulePanel's `toRfc3339` uses, duplicated
// here rather than imported: route/component files in this codebase each
// keep their own small UI helpers rather than sharing them (see
// campaigns.tsx's own note on `platformLabel` for the established reason).
const offsetString = (date: Date): string => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

const localInputToRfc3339 = (localInputValue: string): string => {
  const asLocal = new Date(localInputValue);
  return `${localInputValue}:00${offsetString(asLocal)}`;
};

// Duplicated deliberately -- see campaigns.tsx's own comment on this exact
// ternary; route files do not share UI helpers in this codebase.
const platformLabel = (platform: 'x' | 'pinterest'): string => (platform === 'pinterest' ? 'Pinterest' : 'X');

const scheduleStateDictKey = (state: string): ScheduleStateKey => {
  const keys: ScheduleStateKey[] = ['pending', 'claimed', 'done', 'canceled', 'failed'];
  return (keys as string[]).includes(state) ? (state as ScheduleStateKey) : 'pending';
};

// Fix round 1, Important 2: 9.3 requires "current publication state" on
// every entry, and StateChip.tsx's own rule (quoting the visual spec) is
// that a state's label must always be present and sufficient -- colour
// alone never is, both because a canceled schedule used to render
// identically to a pending one (both got no border-colour modifier below)
// and because colour alone excludes anyone who can't distinguish the
// borders. Rendered in two places: on every grid/list entry (so a glance
// at the calendar shows it, the way `entryModifier`'s border already tried
// and failed to), and again on the selected-entry detail, alongside
// `variantState` -- these are two different vocabularies (a schedule's own
// pending/claimed/done/canceled/failed lifecycle vs. a variant's
// draft/needs_review/scheduled/... one) and neither substitutes for the
// other, so both are shown rather than picking one.
const SCHEDULE_CHIP_MODIFIER: Record<ScheduleStateKey, string | null> = {
  pending: null,
  claimed: 'chip--queued',
  done: 'chip--done',
  canceled: null,
  failed: 'chip--danger',
};

const ScheduleStateLabel = ({ state, dict }: { state: string; dict: Dictionary }): React.JSX.Element => {
  const key = scheduleStateDictKey(state);
  const modifier = SCHEDULE_CHIP_MODIFIER[key];
  return <span className={modifier ? `chip ${modifier}` : 'chip'}>{dict.ui.calendar.scheduleStateLabel[key]}</span>;
};

// The border-left accent stays as supplementary reinforcement (visual
// design spec's own pattern for `.cal-entry`), never the only signal now
// that `ScheduleStateLabel` renders the text every entry needs.
const entryModifier = (entry: CalendarEntry): string => {
  if (entry.scheduleState === 'failed') return 'cal-entry--danger';
  if (entry.approvalRequired) return 'cal-entry--attention';
  if (entry.scheduleState === 'done') return 'cal-entry--done';
  return '';
};

const parseAnchorParam = (raw: string | null, fallback: Date): Date => {
  if (raw !== null) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return fallback;
};

const formatRangeHeading = (view: CalendarView, from: Date, intlLocale: string): string => {
  if (view === 'week') {
    return new Intl.DateTimeFormat(intlLocale, { month: 'short', day: 'numeric', year: 'numeric' }).format(from);
  }
  return new Intl.DateTimeFormat(intlLocale, { month: 'long', year: 'numeric' }).format(from);
};

type LoadState =
  | { status: 'loading'; entries: CalendarEntry[] }
  | { status: 'error'; entries: CalendarEntry[] }
  | { status: 'ready'; entries: CalendarEntry[] };

type ReschedulePending = { variantId: string; from: Date; to: Date };

const EntryButton = ({
  entry,
  dict,
  onMouseDown,
  onOpen,
}: {
  entry: CalendarEntry;
  dict: Dictionary;
  onMouseDown?: (e: React.MouseEvent) => void;
  onOpen: () => void;
}): React.JSX.Element => (
  <div
    role="button"
    tabIndex={0}
    data-testid={`calendar-entry-${entry.scheduleId}`}
    className={`cal-entry ${entryModifier(entry)}`.trim()}
    onMouseDown={onMouseDown}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    }}
  >
    <span className="strong">{entry.projectName}</span>
    <span className="t-meta">{entry.campaignName}</span>
    <span className="mono-meta">{platformLabel(entry.platform)}</span>
    <ScheduleStateLabel state={entry.scheduleState} dict={dict} />
    {entry.approvalRequired && <span className="chip chip--attention">{dict.ui.calendar.entryApprovalRequired}</span>}
  </div>
);

export const CalendarRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const [locale] = useLocale();
  const intlLocale = INTL_LOCALE[locale];
  const [searchParams, setSearchParams] = useSearchParams();

  const view = parseView(searchParams.get('view'));
  const projectFilter = searchParams.get('project') ?? '';
  const platformFilter = searchParams.get('platform') ?? '';
  const stateFilter = searchParams.get('state') ?? '';

  // Fix round 1: `today` used to be a `useMemo(() => new Date(), [])` --
  // computed once at mount and never again, so a tab left open across
  // midnight kept marking yesterday's cell `isToday` and kept defaulting
  // an unset `?date=` to yesterday. Re-read once a minute instead; `load`
  // below depends on the resolved `from`/`to` window, not on `today` or
  // `anchor` directly, so this tick does not by itself trigger a refetch --
  // it only does when the minute crosses an actual week/month boundary.
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setToday(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const anchor = useMemo(() => parseAnchorParam(searchParams.get('date'), today), [searchParams, today]);

  const setParam = useCallback(
    (key: string, value: string): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === '') next.delete(key);
        else next.set(key, value);
        return next;
      });
    },
    [setSearchParams],
  );

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading', entries: [] });
  const [projects, setProjects] = useState<ProjectListRow[]>([]);
  const [dragState, setDragState] = useState<DragState>({ kind: 'idle' });
  const [hoverDayKey, setHoverDayKey] = useState<string | null>(null);
  const [detailEntryId, setDetailEntryId] = useState<string | null>(null);
  const [rescheduleInput, setRescheduleInput] = useState('');
  const [pendingReschedule, setPendingReschedule] = useState<ReschedulePending | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(false);

  const { from, to } = rangeFor(view, anchor);

  const load = useCallback((): void => {
    setLoadState((prev) => ({ status: 'loading', entries: prev.entries }));
    fetchCalendar({ from: from.toISOString(), to: to.toISOString() })
      .then((res) => setLoadState({ status: 'ready', entries: res.entries }))
      .catch(() => setLoadState({ status: 'error', entries: [] }));
    // Depends on the resolved window, not on `view`/`anchor` directly: for
    // week and month views alike, `from`/`to` only actually change at a
    // real range boundary (a new week, a new month, or an explicit
    // previous/next/today click) -- the `today` tick above changes
    // `anchor`'s object identity every minute without changing which week
    // or month it resolves to, so keying off `from`/`to` themselves is what
    // keeps that tick from refetching the same range on a timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, from.getTime(), to.getTime()]);

  useEffect(load, [load]);

  useEffect(() => {
    fetchProjects()
      .then((res) => setProjects(res.projects))
      .catch(() => setProjects([]));
  }, []);

  const visibleEntries = useMemo(
    () => filterEntries(loadState.entries, { projectId: projectFilter, platform: platformFilter, state: stateFilter }),
    [loadState.entries, projectFilter, platformFilter, stateFilter],
  );

  const status = rangeStatus(loadState.entries.length, visibleEntries.length, loadState.status === 'error');

  const dayCells: DayCell[] = view === 'month' ? monthCells(anchor, today) : weekCells(anchor, today);

  const entriesByDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const entry of visibleEntries) {
      const key = localDayKey(new Date(entry.publishAtUtc));
      const bucket = map.get(key);
      if (bucket) bucket.push(entry);
      else map.set(key, [entry]);
    }
    return map;
  }, [visibleEntries]);

  const detailEntry = detailEntryId !== null ? (loadState.entries.find((e) => e.scheduleId === detailEntryId) ?? null) : null;

  const openDetail = (scheduleId: string): void => {
    setDetailEntryId(scheduleId);
    setRescheduleInput('');
    setCancelError(false);
    setRescheduleError(false);
  };

  const handleEntryMouseDown = (e: React.MouseEvent, entry: CalendarEntry): void => {
    e.preventDefault();
    setDragState((s) => onPointerDown(s, entry.scheduleId, { x: e.clientX, y: e.clientY }));
  };

  const handleGridMouseMove = (e: React.MouseEvent): void => {
    if (dragState.kind === 'idle') return;
    setDragState((s) => onPointerMove(s, { x: e.clientX, y: e.clientY }));
  };

  const handleGridMouseUp = (): void => {
    const wasPressed = dragState.kind === 'pressed';
    const pressedEntryId = dragState.kind !== 'idle' ? dragState.entryId : null;
    const { next, droppedEntryId } = onPointerUp(dragState);
    setDragState(next);

    if (droppedEntryId !== null) {
      const entry = visibleEntries.find((e) => e.scheduleId === droppedEntryId);
      const targetCell = hoverDayKey !== null ? dayCells.find((c) => localDayKey(c.date) === hoverDayKey) : undefined;
      setHoverDayKey(null);
      // A drop opens the confirmation; it never saves by itself (Ruling 2).
      if (entry && targetCell) {
        const fromInstant = new Date(entry.publishAtUtc);
        const toInstant = computeDroppedInstant(fromInstant, targetCell.date);
        setRescheduleError(false);
        setPendingReschedule({ variantId: entry.variantId, from: fromInstant, to: toInstant });
      }
      return;
    }

    if (wasPressed && pressedEntryId !== null) {
      openDetail(pressedEntryId);
    }
  };

  const handleKeyboardReschedule = (): void => {
    if (!detailEntry || rescheduleInput === '') return;
    let toInstant: Date;
    try {
      toInstant = normalizeToUtc(localInputToRfc3339(rescheduleInput));
    } catch {
      return;
    }
    setRescheduleError(false);
    setPendingReschedule({
      variantId: detailEntry.variantId,
      from: new Date(detailEntry.publishAtUtc),
      to: toInstant,
    });
  };

  // Fix round 1, Important 1: this used to be a bare `await scheduleVariant(...)`
  // with no try/catch, called through `RescheduleConfirm`'s own
  // `void onConfirm(to)` (that component's contract from Task 5 -- it does
  // not itself track a busy/error state for its caller's mutation). A
  // rejected `scheduleVariant` call (the reviewer's repro: a 422 for a
  // past `publishAt`) left the dialog open with zero on-screen indication
  // that anything had failed, Confirm still clickable, and an unhandled
  // rejection in the console -- the operator's click appeared to do
  // nothing. Mirrors `handleCancelSchedule` below: a busy guard against a
  // duplicate submit while the request is in flight, and an error flag
  // that renders next to the dialog on failure. Unlike cancel, a failed
  // reschedule does *not* close `pendingReschedule` -- the operator needs
  // the dialog to stay open so Confirm (retry) and Cancel are both still
  // there, per 9.6's "each exception state offers the next relevant
  // action."
  const handleConfirmReschedule = async (variantId: string, toInstant: Date): Promise<void> => {
    if (rescheduleBusy) return;
    setRescheduleBusy(true);
    setRescheduleError(false);
    try {
      await scheduleVariant(variantId, rfc3339FromInstant(toInstant));
      setPendingReschedule(null);
      setDetailEntryId(null);
      load();
    } catch {
      setRescheduleError(true);
    } finally {
      setRescheduleBusy(false);
    }
  };

  const handleCancelSchedule = async (): Promise<void> => {
    if (!detailEntry) return;
    setCancelBusy(true);
    setCancelError(false);
    try {
      await cancelSchedule(detailEntry.variantId);
      setDetailEntryId(null);
      load();
    } catch {
      setCancelError(true);
    } finally {
      setCancelBusy(false);
    }
  };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const scheduleStateOptions: ScheduleStateKey[] = ['pending', 'claimed', 'done', 'canceled', 'failed'];

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="between">
        <div className="stack-1">
          <h1 className="t-title">{dict.ui.calendar.title}</h1>
          <span className="t-meta">
            {dict.ui.workspaceAllProjects} · {formatRangeHeading(view, from, intlLocale)}
          </span>
        </div>
      </div>

      <div className="between wrap" style={{ gap: 'var(--sp-3)' }}>
        <div className="tabs" role="tablist">
          {(['week', 'month', 'list'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className="tab"
              onClick={() => setParam('view', v)}
            >
              {v === 'week' ? dict.ui.calendar.viewWeek : v === 'month' ? dict.ui.calendar.viewMonth : dict.ui.calendar.viewList}
            </button>
          ))}
        </div>

        <div className="row" style={{ gap: 'var(--sp-2)' }}>
          <button type="button" className="btn" onClick={() => setParam('date', localDayKey(shiftAnchor(view, anchor, -1)))}>
            {dict.ui.calendar.previous}
          </button>
          <button type="button" className="btn" onClick={() => setParam('date', '')}>
            {dict.ui.calendar.today}
          </button>
          <button type="button" className="btn" onClick={() => setParam('date', localDayKey(shiftAnchor(view, anchor, 1)))}>
            {dict.ui.calendar.next}
          </button>
        </div>
      </div>

      <div className="row wrap" style={{ gap: 'var(--sp-3)' }}>
        <div className="field" style={{ maxWidth: 220 }}>
          <label className="field__label" htmlFor="calendar-filter-project">{dict.ui.calendar.filterProjectLabel}</label>
          <select
            id="calendar-filter-project"
            className="select"
            value={projectFilter}
            onChange={(e) => setParam('project', e.target.value)}
          >
            <option value="">{dict.ui.calendar.filterProjectAll}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div className="field" style={{ maxWidth: 180 }}>
          <label className="field__label" htmlFor="calendar-filter-platform">{dict.ui.calendar.filterPlatformLabel}</label>
          <select
            id="calendar-filter-platform"
            className="select"
            value={platformFilter}
            onChange={(e) => setParam('platform', e.target.value)}
          >
            <option value="">{dict.ui.calendar.filterPlatformAll}</option>
            <option value="x">{platformLabel('x')}</option>
            <option value="pinterest">{platformLabel('pinterest')}</option>
          </select>
        </div>

        <div className="field" style={{ maxWidth: 180 }}>
          <label className="field__label" htmlFor="calendar-filter-state">{dict.ui.calendar.filterStateLabel}</label>
          <select
            id="calendar-filter-state"
            className="select"
            value={stateFilter}
            onChange={(e) => setParam('state', e.target.value)}
          >
            <option value="">{dict.ui.calendar.filterStateAll}</option>
            {scheduleStateOptions.map((s) => (
              <option key={s} value={s}>{dict.ui.calendar.scheduleStateLabel[s]}</option>
            ))}
          </select>
        </div>
      </div>

      <span className="mono-meta">{dict.ui.calendar.timezoneNotice(tz)}</span>

      {loadState.status === 'loading' && loadState.entries.length === 0 && <p className="t-meta">{dict.ui.calendar.loading}</p>}

      {status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.calendar.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.calendar.retry}</button>
          </div>
        </div>
      )}

      {status === 'empty' && (
        <div className="empty">
          <div className="empty__title">{dict.ui.calendar.noPostsTitle}</div>
          <div className="empty__body">{dict.ui.calendar.noPostsBody}</div>
        </div>
      )}

      {status === 'filtered_out' && (
        <div className="empty">
          <div className="empty__title">{dict.ui.calendar.filteredEmptyTitle}</div>
          <div className="empty__body">{dict.ui.calendar.filteredEmptyBody}</div>
        </div>
      )}

      {status === 'has_entries' && view !== 'list' && (
        <div
          className="panel"
          onMouseMove={handleGridMouseMove}
          onMouseUp={handleGridMouseUp}
          onMouseLeave={dragState.kind === 'dragging' ? handleGridMouseUp : undefined}
        >
          <div className="panel__body" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 'var(--sp-2)' }}>
            {dayCells.map((cell) => {
              const key = localDayKey(cell.date);
              const dayEntries = entriesByDay.get(key) ?? [];
              return (
                <div
                  key={key}
                  data-testid={`calendar-day-${key}`}
                  onMouseEnter={() => { if (dragState.kind === 'dragging') setHoverDayKey(key); }}
                  style={{
                    minHeight: 96,
                    padding: 'var(--sp-1)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-control)',
                    opacity: cell.inCurrentPeriod ? 1 : 0.45,
                    background: cell.isToday ? 'var(--surface-sunken)' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-1)',
                  }}
                >
                  <span className="t-meta">{cell.date.getDate()}</span>
                  {dayEntries.map((entry) => (
                    <EntryButton
                      key={entry.scheduleId}
                      entry={entry}
                      dict={dict}
                      onMouseDown={(e) => handleEntryMouseDown(e, entry)}
                      onOpen={() => openDetail(entry.scheduleId)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {status === 'has_entries' && view === 'list' && (
        <div className="stack" style={{ gap: 'var(--sp-4)' }}>
          {[...entriesByDay.entries()]
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([key, dayEntries]) => (
              <div key={key} className="panel">
                <div className="panel__head">
                  <span className="t-section">
                    {new Intl.DateTimeFormat(intlLocale, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(dayEntries[0]!.publishAtUtc))}
                  </span>
                </div>
                <div className="panel__body stack-1">
                  {dayEntries.map((entry) => (
                    <EntryButton key={entry.scheduleId} entry={entry} dict={dict} onOpen={() => openDetail(entry.scheduleId)} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {detailEntry && (
        <div className="panel" data-testid="calendar-entry-detail">
          <div className="panel__head">
            <h2 className="t-section">{dict.ui.calendar.detailTitle}</h2>
            <button type="button" className="btn btn--ghost" onClick={() => setDetailEntryId(null)}>{dict.ui.calendar.detailClose}</button>
          </div>
          <div className="panel__body stack">
            <dl className="kv">
              <dt>{dict.ui.calendar.detailProjectLabel}</dt><dd>{detailEntry.projectName}</dd>
              <dt>{dict.ui.calendar.detailCampaignLabel}</dt><dd>{detailEntry.campaignName}</dd>
              <dt>{dict.ui.calendar.detailPlatformLabel}</dt><dd>{platformLabel(detailEntry.platform)}</dd>
              <dt>{dict.ui.calendar.detailStateLabel}</dt><dd><StateChip state={detailEntry.variantState} /></dd>
              <dt>{dict.ui.calendar.detailScheduleStateLabel}</dt><dd><ScheduleStateLabel state={detailEntry.scheduleState} dict={dict} /></dd>
            </dl>

            <Stamp instant={detailEntry.publishAtUtc} />

            {detailEntry.approvalRequired && (
              <div className="alert alert--attention">
                <div className="alert__body">{dict.ui.calendar.entryApprovalRequired}</div>
              </div>
            )}

            {detailEntry.textExcerpt && <p className="t-body">{detailEntry.textExcerpt}</p>}

            <div className="field" style={{ maxWidth: 320 }}>
              <label className="field__label" htmlFor="calendar-reschedule-input">{dict.ui.calendar.newTimeLabel}</label>
              <input
                id="calendar-reschedule-input"
                type="datetime-local"
                className="input"
                value={rescheduleInput}
                onChange={(e) => setRescheduleInput(e.target.value)}
              />
            </div>

            <div className="row" style={{ gap: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--primary"
                disabled={rescheduleInput === ''}
                onClick={handleKeyboardReschedule}
              >
                {dict.actions.reschedule}
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={cancelBusy}
                onClick={() => { void handleCancelSchedule(); }}
              >
                {dict.actions.cancelSchedule}
              </button>
            </div>
            {cancelError && <div className="field__error">{dict.ui.calendar.cancelFailed}</div>}
          </div>
        </div>
      )}

      {pendingReschedule && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div className="stack" style={{ maxWidth: 480, width: '100%', gap: 'var(--sp-3)' }}>
            <RescheduleConfirm
              from={pendingReschedule.from}
              to={pendingReschedule.to}
              onCancel={() => setPendingReschedule(null)}
              onConfirm={async (toInstant) => {
                await handleConfirmReschedule(pendingReschedule.variantId, toInstant);
              }}
            />
            {rescheduleError && (
              <div className="alert alert--danger" data-testid="reschedule-error">
                <div className="alert__body">{dict.ui.calendar.rescheduleFailed}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
